import React, { useCallback, useEffect, useState } from 'react';
import { CreateFormFeatureDialog } from 'components';
import { useLocation, useNavigate } from 'react-router-dom';
import CodeMirror from '@uiw/react-codemirror';
import { PIPELINES_AND_STEPS_PATHS } from 'constants/pipelines-and-steps-paths';
import {
    commonStyles,
    PipelinesCodeMirrorStyles,
    RULE_SET,
} from 'pages/pipelines/pipelines-helper';
import { Box, Grid } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { ErrorMessage, Fab, TextField as MuiTextField } from '@biq/apex-ui';
import { pipelineFormStyles as formStyles } from 'pages/pipelines/CreateOrUpdatePipeline/components/pipeline-form-helper';
import {
    createRuleSetConfig,
    CreateRuleSetDefaults,
    CreateRuleSetSchema,
    DROOLS_DOCS_LINK,
    ruleSetFormStyles,
    RuleSetFormValues,
} from './create-rule-form-helper';
import { yupResolver } from '@hookform/resolvers/yup';
import { JobFieldHeadingWithTooltip } from 'pages';
import { CodeMirrorStyles, validationsMessages } from 'lib';
import { createPipelineStyles as createFormFeatureStyles } from 'pages/pipelines/CreateOrUpdatePipeline/create-or-update-pipeline-helper';
import { createFieldMappingStyles } from '../createFieldMapping/field-mapping-form-helper';
import {
    PostRuleProps,
    PostRuleSetStepProps,
    RuleStepResponse,
    useCreateRule,
    useCreateRuleSet,
    useUpdateRule,
} from 'hooks';
import { useAuth, useServicesEnabled } from '../../../../core';
import { CreateStepFormData } from '../components/step-form-helper';

export const CreateRuleForm = ({
    isEdit = false,
    stepData,
    onModalClose,
    onAddNewOrUpdateRule,
    ruleDetails,
}: {
    isEdit: boolean;
    stepData?: CreateStepFormData;
    onModalClose: () => void;
    onAddNewOrUpdateRule?: (rule: RuleStepResponse) => void;
    ruleDetails?: RuleStepResponse;
}): React.JSX.Element => {
    const { isServiceEnabledFor } = useServicesEnabled();
    const isDataPipelineServiceEnabled = isServiceEnabledFor('DATA_PIPELINE');
    const { getToken } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const {
        control,
        handleSubmit,
        clearErrors,
        watch,
        setValue,
        formState: { errors },
    } = useForm<RuleSetFormValues>({
        mode: 'all',
        resolver: yupResolver(CreateRuleSetSchema),
        defaultValues: CreateRuleSetDefaults,
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { mutate: createRule } = useCreateRule();
    const { mutate: updateRule } = useUpdateRule();
    const { mutate: createRuleSetStep } = useCreateRuleSet();
    const whenBase64 = watch('whenBase64');
    const thenBase64 = watch('thenBase64');

    useEffect(() => {
        if (ruleDetails) {
            setValue('ruleName', ruleDetails.name);
            setValue('description', ruleDetails.description);
            setValue('whenBase64', atob(ruleDetails.whenBase64));
            setValue('thenBase64', atob(ruleDetails.thenBase64));
        }
    }, [ruleDetails]);

    const onWhenValueChange = useCallback((value: string) => {
        setValue('whenBase64', value);
        clearErrors('whenBase64');
    }, []);

    const onThenValueChange = useCallback((value: string) => {
        setValue('thenBase64', value);
        clearErrors('thenBase64');
    }, []);

    const handleBackButtonClick = (): void => {
        onModalClose?.();
    };

    const handleCloseButtonClick = (): void => {
        if (location.pathname.includes('steps/edit')) {
            onModalClose();
        } else {
            navigate(PIPELINES_AND_STEPS_PATHS.LANDING_PAGE);
        }
    };

    const onSubmit = (data: RuleSetFormValues): void => {
        setIsLoading(true);

        const postRuleData: PostRuleProps = {
            name: data.ruleName,
            description: data.description ?? '',
            whenBase64: btoa(data.whenBase64),
            thenBase64: btoa(data.thenBase64),
            importSet: [],
            globalSet: [],
        };

        const postStepData: PostRuleSetStepProps = {
            name: stepData?.name ?? '',
            description: stepData?.description ?? '',
            ruleIds: [],
        };

        if (isEdit) {
            const putData = {
                id: ruleDetails?.id as string,
                name: data.ruleName,
                description: data.description ?? '',
                whenBase64: btoa(data.whenBase64),
                thenBase64: btoa(data.thenBase64),
                importSet: ruleDetails?.importSet ?? [],
                globalSet: ruleDetails?.globalSet ?? [],
            };
            updateRule(
                { putData, token: getToken() },
                {
                    onSuccess: () => {
                        setIsLoading(false);
                        onAddNewOrUpdateRule?.(putData);
                        onModalClose();
                    },
                    onError: () => {
                        setIsLoading(false);
                    },
                }
            );
        } else {
            createRule(
                { postData: postRuleData, token: getToken() },
                {
                    onSuccess: (ruleResult: any) => {
                        postStepData.ruleIds = [ruleResult.data.id];
                        if (location.pathname.includes('step-details')) {
                            createRuleSetStep(
                                { postData: postStepData, token: getToken() },
                                {
                                    onSuccess: (stepResult: any) => {
                                        setIsLoading(false);
                                        const path =
                                            PIPELINES_AND_STEPS_PATHS.UPDATE_STEP_DETAILS.replace(
                                                ':id',
                                                stepResult.data.id
                                            );
                                        const query = `?option=${RULE_SET}`;

                                        navigate(`${path}${query}`);
                                    },
                                    onError: () => {
                                        setIsLoading(false);
                                    },
                                }
                            );
                        } else if (location.pathname.includes('steps/edit')) {
                            onAddNewOrUpdateRule?.(ruleResult.data);
                            onModalClose();
                        }
                    },
                    onError: () => {
                        setIsLoading(false);
                    },
                }
            );
        }
    };

    const handleDroolsDocsClick = (): void => {
        window.open(DROOLS_DOCS_LINK, '_blank');
    };

    if (!isDataPipelineServiceEnabled) {
        navigate(PIPELINES_AND_STEPS_PATHS.LANDING_PAGE);
        return <></>;
    }

    return (
        <CreateFormFeatureDialog
            buttonText={isEdit ? 'Update' : 'Save'}
            isLoading={isLoading}
            handleSubmit={handleSubmit(onSubmit)}
            handleCloseButton={handleCloseButtonClick}
            handleBackButton={handleBackButtonClick}
            listConfig={createRuleSetConfig}
            isOpenInModal={true}
            customStyles={createFormFeatureStyles.root}
            customButtonContainerStyles={
                createFieldMappingStyles.formFeatureNextButtonStyles
            }
        >
            <Grid
                sx={{
                    ...formStyles.mainContainer,
                    position: 'relative',
                }}
            >
                <Box sx={formStyles.fieldGroup}>
                    <Controller
                        name='ruleName'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <MuiTextField
                                {...field}
                                variant='outlined'
                                label='Rule Name'
                                value={field.value}
                                onChange={(event): void => {
                                    field.onChange(event);
                                }}
                                onBlur={(value): void => {
                                    field.onChange(value.target.value.trim());
                                }}
                                required
                                error={!!errors?.ruleName}
                                errorText={errors?.ruleName?.message ?? ''}
                            />
                        )}
                    />
                </Box>

                <Box sx={formStyles.fieldGroup}>
                    <Controller
                        name='description'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <MuiTextField
                                {...field}
                                variant='outlined'
                                label='Description'
                                value={field.value}
                                onChange={(event): void => {
                                    field.onChange(event);
                                }}
                                onBlur={(value): void => {
                                    field.onChange(value.target.value.trim());
                                }}
                                minRows={4}
                                multiline
                                error={!!errors?.description}
                                errorText={errors?.description?.message ?? ''}
                            />
                        )}
                    />
                </Box>

                <Box sx={formStyles.fieldGroup}>
                    <Box>
                        <JobFieldHeadingWithTooltip
                            label='when'
                            title={'This is a drools MVEL dialect rule.'}
                            required
                            popperStyles={ruleSetFormStyles.popperStyles}
                            modifiers={[
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, 4],
                                    },
                                },
                            ]}
                        />
                    </Box>
                    <Controller
                        name='whenBase64'
                        control={control}
                        render={(): React.JSX.Element => (
                            <>
                                <CodeMirror
                                    value={whenBase64}
                                    style={{
                                        ...commonStyles.codeMirrorConatiner,
                                        ...(errors.whenBase64
                                            ? commonStyles.codeMirrorError
                                            : {}),
                                    }}
                                    height={ruleSetFormStyles.codeMirrorHeight}
                                    extensions={[
                                        CodeMirrorStyles,
                                        PipelinesCodeMirrorStyles,
                                    ]}
                                    onChange={onWhenValueChange}
                                />
                                {errors?.whenBase64 && (
                                    <Box pt={0.25}>
                                        <ErrorMessage
                                            errorMessage={
                                                validationsMessages.required
                                            }
                                        />
                                    </Box>
                                )}
                            </>
                        )}
                    />
                </Box>

                <Box sx={formStyles.fieldGroup}>
                    <Box>
                        <JobFieldHeadingWithTooltip
                            label='then'
                            title={'This is a drools MVEL dialect rule.'}
                            required
                            popperStyles={ruleSetFormStyles.popperStyles}
                            modifiers={[
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [1, 4],
                                    },
                                },
                            ]}
                        />
                    </Box>
                    <Controller
                        name='thenBase64'
                        control={control}
                        render={(): React.JSX.Element => (
                            <>
                                <CodeMirror
                                    value={thenBase64}
                                    style={{
                                        ...commonStyles.codeMirrorConatiner,
                                        ...(errors.thenBase64
                                            ? commonStyles.codeMirrorError
                                            : {}),
                                    }}
                                    height={ruleSetFormStyles.codeMirrorHeight}
                                    extensions={[
                                        CodeMirrorStyles,
                                        PipelinesCodeMirrorStyles,
                                    ]}
                                    onChange={onThenValueChange}
                                />
                                {errors?.thenBase64 && (
                                    <Box pt={0.25}>
                                        <ErrorMessage
                                            errorMessage={
                                                validationsMessages.required
                                            }
                                        />
                                    </Box>
                                )}
                            </>
                        )}
                    />
                </Box>

                <Box sx={ruleSetFormStyles.droolsDocsContainer}>
                    <Fab
                        type='button'
                        sx={{
                            ...commonStyles.addButtonStyles,
                            ...ruleSetFormStyles.droolsDocsButtonStyles,
                        }}
                        icon=''
                        color='secondary'
                        label='Drools docs'
                        onClick={(): void => {
                            handleDroolsDocsClick();
                        }}
                    />
                </Box>
            </Grid>
        </CreateFormFeatureDialog>
    );
};
